import React from 'react';
import { Calendar, MapPin, Trophy, Star, Users, DollarSign } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const TournamentHero = ({ tournament }) => {
  const { isDarkMode } = useTheme();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Mens_Open':
        return 'bg-fsgu-blue-100 text-fsgu-blue-700 dark:bg-fsgu-blue-900/30 dark:text-fsgu-blue-300';
      case 'Ladies_Open':
        return 'bg-fsgu-gold-100 text-fsgu-gold-700 dark:bg-fsgu-gold-900/30 dark:text-fsgu-gold-300';
      case 'Junior_Boys':
      case 'Junior_Girls':
        return 'bg-fsgu-green-100 text-fsgu-green-700 dark:bg-fsgu-green-900/30 dark:text-fsgu-green-300';
      case 'Mens_Mid_Am':
        return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300';
      case 'Ladies_Mid_Am':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      case 'Mens_Senior':
      case 'Ladies_Senior':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      case 'Mens_Super_Senior':
      case 'Ladies_Super_Senior':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
      case 'Country_Districts':
        return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
      case 'Mixed':
        return 'bg-fsgu-silver-200 text-fsgu-silver-700 dark:bg-fsgu-gray-700 dark:text-fsgu-silver-300';
      default:
        return 'bg-fsgu-silver-200 text-fsgu-silver-700 dark:bg-fsgu-gray-700 dark:text-fsgu-silver-300';
    }
  };

  const formatCategoryLabel = (category) => {
    const labels = {
      'Mens_Open': "Men's Open",
      'Mens_Mid_Am': "Men's Mid-Am",
      'Mens_Senior': "Men's Senior",
      'Mens_Super_Senior': "Men's Super Senior",
      'Ladies_Open': "Ladies Open",
      'Ladies_Mid_Am': "Ladies Mid-Am",
      'Ladies_Senior': "Ladies Senior",
      'Ladies_Super_Senior': "Ladies Super Senior",
      'Junior_Boys': "Junior Boys",
      'Junior_Girls': "Junior Girls",
      'Mixed': "Mixed",
      'Country_Districts': "Country Districts"
    };
    return labels[category] || category?.replace(/_/g, ' ') || 'Open';
  };

  return (
    <div className="relative">
      {/* Banner Image - Desktop and Mobile versions */}
      <div className="relative h-64 sm:h-80 lg:h-96 xl:h-[40rem] overflow-hidden bg-gradient-to-br from-fsgu-green-600 to-fsgu-blue-600">
        {/* Desktop Image (xl and above) */}
        <img
          src="/tournamentHeroDesk.png"
          alt={tournament.tournament_name}
          className="hidden xl:block w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        {/* Mobile/Tablet Image (below xl) */}
        <img
          src="/tournamentHero.png"
          alt={tournament.tournament_name}
          className="xl:hidden w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        
        {/* Badges Overlay */}
        <div className="absolute top-4 right-4 flex gap-2">
          {tournament.is_featured && (
            <div className={`p-2 rounded-lg ${
              isDarkMode ? 'bg-fsgu-gray-900/80' : 'bg-white/80'
            } backdrop-blur-sm`}>
              <Star className="w-5 h-5 text-fsgu-gold-500" fill="currentColor" />
            </div>
          )}
          {tournament.is_championship && (
            <div className={`p-2 rounded-lg ${
              isDarkMode ? 'bg-fsgu-gray-900/80' : 'bg-white/80'
            } backdrop-blur-sm`}>
              <Trophy className="w-5 h-5 text-fsgu-green-500" />
              </div>
            )}
          </div>

          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
            <div className="container-custom">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  getCategoryColor(tournament.category)
                }`}>
                  {formatCategoryLabel(tournament.category)}
                </span>
                
                {tournament.registration_status === 'open' && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    isDarkMode
                      ? 'bg-fsgu-green-900/30 text-fsgu-green-400'
                      : 'bg-fsgu-green-100 text-fsgu-green-700'
                  }`}>
                    Registration Open
                  </span>
                )}
                
                {tournament.is_upcoming && tournament.starts_in_days <= 30 && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    isDarkMode
                      ? 'bg-fsgu-gold-900/30 text-fsgu-gold-400'
                      : 'bg-fsgu-gold-100 text-fsgu-gold-700'
                  }`}>
                    Starts in {tournament.starts_in_days} days
                  </span>
                )}
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">
                {tournament.tournament_name}
              </h1>
              
              {tournament.tournament_short_name && (
                <p className="text-lg sm:text-xl text-fsgu-silver-300">
                  {tournament.tournament_short_name}
                </p>
              )}
            </div>
          </div>
        </div>

      {/* Quick Info Bar */}
      <div className={`border-b ${
        isDarkMode ? 'bg-fsgu-gray-800 border-fsgu-gray-700' : 'bg-fsgu-white border-fsgu-silver-200'
      }`}>
        <div className="container-custom py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Dates */}
            <div className="flex items-center">
              <Calendar className={`w-5 h-5 mr-3 flex-shrink-0 ${
                isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
              }`} />
              <div>
                <div className={`text-xs ${
                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                }`}>
                  Tournament Dates
                </div>
                <div className={`text-sm font-medium ${
                  isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                }`}>
                  {formatDate(tournament.start_date)}
                  {tournament.start_date !== tournament.end_date && (
                    <> - {formatDate(tournament.end_date)}</>
                  )}
                </div>
              </div>
            </div>

            {/* Venue */}
            <div className="flex items-center">
              <MapPin className={`w-5 h-5 mr-3 flex-shrink-0 ${
                isDarkMode ? 'text-fsgu-blue-400' : 'text-fsgu-blue-500'
              }`} />
              <div>
                <div className={`text-xs ${
                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                }`}>
                  Venue
                </div>
                <div className={`text-sm font-medium truncate ${
                  isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                }`}>
                  {tournament.venue_name}
                </div>
              </div>
            </div>

            {/* Entries */}
            {tournament.field_size && (
              <div className="flex items-center">
                <Users className={`w-5 h-5 mr-3 flex-shrink-0 ${
                  isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-500'
                }`} />
                <div>
                  <div className={`text-xs ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                  }`}>
                    Field Size
                  </div>
                  <div className={`text-sm font-medium ${
                    isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                  }`}>
                    {tournament.confirmed_entries} / {tournament.field_size}
                    {tournament.is_full && (
                      <span className="ml-2 text-xs text-red-500">FULL</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Entry Fee */}
            {tournament.entry_fee > 0 && (
              <div className="flex items-center">
                <DollarSign className={`w-5 h-5 mr-3 flex-shrink-0 ${
                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                }`} />
                <div>
                  <div className={`text-xs ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                  }`}>
                    Entry Fee
                  </div>
                  <div className={`text-sm font-medium ${
                    isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                  }`}>
                    {tournament.currency} {tournament.entry_fee.toFixed(2)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentHero;